import { visionLabApi } from '@/app/apiClient';
// The entry build-task view is shared with the delivery-entry read, so its
// contract lives in the delivery-core `contracts.ts`, not in outputContracts.
import type { EntryTask } from '@/features/delivery/types/contracts';
import {
  type BuildScopeInput,
  type ChangeOutputConfigInput,
  type CustomerOutputOrphanPage,
  type DeliveryFinalizeResult,
  type DeliveryOutputConfig,
  type MetadataStatus,
  type OutputBuildResult,
  type OutputBuildState,
  type OutputEntryBuildResult,
  type OutputEntryMetadataResultResponse,
  type OutputEntryPageResponse,
  type OutputManifestMode,
  type OutputManifestReport,
  type OutputReadLease,
  type OutputSummary,
  type PatchOutputEntryMetadataInput,
  type PurgeOrphansInput,
  type PurgeOrphansResult,
  type RerunOutputEntryInput,
  type ResolveOutputMetadataResult,
  buildScopeInputSchema,
  changeOutputConfigInputSchema,
  outputManifestModeSchema,
  patchOutputEntryMetadataInputSchema,
  purgeOrphansInputSchema,
  rerunOutputEntryInputSchema,
} from '@/features/delivery/types/outputContracts';
import type { ApiCallOptions } from '@/shared/api/client';

/**
 * Transport-only bindings for the v2 DeliveryOutputConfig routes
 * (`/deliveries/:id/output…`, see `DeliveryCoreRoutes.lean`).
 */

const enc = encodeURIComponent;

export async function fetchDeliveryOutputConfig(
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<DeliveryOutputConfig | null> {
  return visionLabApi.get<DeliveryOutputConfig | null>(
    `/deliveries/${enc(deliveryId)}/output-config`,
    options,
  );
}

export async function changeOutputConfig(
  deliveryId: string,
  input: ChangeOutputConfigInput,
  options: ApiCallOptions = {},
): Promise<DeliveryOutputConfig> {
  const body = changeOutputConfigInputSchema.parse(input);
  return visionLabApi.patch<DeliveryOutputConfig>(`/deliveries/${enc(deliveryId)}/output-config`, {
    ...options,
    json: body,
  });
}

/**
 * A output's rollup — every count and every `duration.*Ms` — for the whole output, or
 * narrowed to one derived batch by `?batchNo=` (backend `fb3b2cc0`). Response shape is
 * identical either way.
 *
 * An absent batch is `200` with all-zero counts, not a 404: a batch is a grouping, so
 * "no entries with that number" is an answer and not a missing object. A non-integer or
 * negative `batchNo` is a 400 and never falls back to the whole-output answer.
 *
 * CAUTION — this narrowing is NOT safe to send blind. An older backend (product is on
 * `sha-2b31589`) ignores the unknown param and answers `200` with the WHOLE-output
 * numbers, so a batch-scoped caller would render the wrong figures with no error to
 * notice. The only caller therefore states the scope it is showing in its own label; see
 * `useOutputSummary`.
 */
export async function fetchOutputSummary(
  deliveryId: string,
  { batchNo }: { batchNo?: number } = {},
  options: ApiCallOptions = {},
): Promise<OutputSummary> {
  return visionLabApi.get<OutputSummary>(`/deliveries/${enc(deliveryId)}/outputs/summary`, {
    ...options,
    searchParams: { batchNo },
  });
}

/** Preview or publish one customer `manifest.json` per Batch in the Output. */
export async function publishOutputManifests(
  deliveryId: string,
  mode: OutputManifestMode,
  options: ApiCallOptions = {},
): Promise<OutputManifestReport> {
  const admittedMode = outputManifestModeSchema.parse(mode);
  return visionLabApi.post<OutputManifestReport>(`/deliveries/${enc(deliveryId)}/manifests`, {
    ...options,
    searchParams: { mode: admittedMode },
  });
}

/** The same three build scopes, narrowed to one derived batch. */
export async function buildOutputBatch(
  deliveryId: string,
  batchNo: number,
  input: BuildScopeInput = {},
  options: ApiCallOptions = {},
): Promise<OutputBuildResult> {
  const body = buildScopeInputSchema.parse(input);
  return visionLabApi.post<OutputBuildResult>(
    `/deliveries/${enc(deliveryId)}/batches/${batchNo}/build`,
    { ...options, json: body },
  );
}

export async function buildOutputEntry(
  deliveryId: string,
  entryId: string,
  options: ApiCallOptions = {},
): Promise<OutputEntryBuildResult> {
  return visionLabApi.post<OutputEntryBuildResult>(
    `/deliveries/${enc(deliveryId)}/entries/${enc(entryId)}/build`,
    { ...options },
  );
}

/**
 * Re-run ONE output entry (`POST .../entries/:entryId/rerun`) — the single route
 * that replaced the per-entry `retry` (now `scope: 'failed'`) and `rebuild` (now
 * `scope: 'terminal'`). Reuses the 202 enqueue shape of build.
 *
 * NOT to be confused with `rebuildDeliveryEntry` in `api/deliveries.ts`: that is the
 * *delivery*-level entry rebuild (`POST /deliveries/entries/:id/rebuild`, body
 * `{expectedGeneration, reason}`) and it did not change.
 */
export async function rerunOutputEntry(
  deliveryId: string,
  entryId: string,
  input: RerunOutputEntryInput,
  options: ApiCallOptions = {},
): Promise<OutputEntryBuildResult> {
  const body = rerunOutputEntryInputSchema.parse(input);
  return visionLabApi.post<OutputEntryBuildResult>(
    `/deliveries/${enc(deliveryId)}/entries/${enc(entryId)}/rerun`,
    { ...options, json: body },
  );
}

/** Re-resolve (re-bake) an entry's metadata snapshot from the delivery binding (no body). */
export async function resolveOutputEntryMetadata(
  deliveryId: string,
  entryId: string,
  options: ApiCallOptions = {},
): Promise<OutputEntryMetadataResultResponse> {
  return visionLabApi.post<OutputEntryMetadataResultResponse>(
    `/deliveries/${enc(deliveryId)}/entries/${enc(entryId)}/metadata/resolve`,
    { ...options },
  );
}

/** Re-resolve (re-bake) every entry's metadata snapshot in a output (no body). */
export async function resolveOutputMetadata(
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<ResolveOutputMetadataResult> {
  return visionLabApi.post<ResolveOutputMetadataResult>(
    `/deliveries/${enc(deliveryId)}/metadata/resolve`,
    {
      ...options,
    },
  );
}

/** Shallow-merge customer metadata overrides into an entry's snapshot. */
export async function patchOutputEntryMetadata(
  deliveryId: string,
  entryId: string,
  values: PatchOutputEntryMetadataInput,
  options: ApiCallOptions = {},
): Promise<OutputEntryMetadataResultResponse> {
  const body = patchOutputEntryMetadataInputSchema.parse(values);
  return visionLabApi.patch<OutputEntryMetadataResultResponse>(
    `/deliveries/${enc(deliveryId)}/entries/${enc(entryId)}/metadata`,
    { ...options, json: body },
  );
}

export interface ListOutputEntriesParams {
  limit?: number;
  cursor?: string | null;
  /** Free-text filter, matched by the backend against the entry/segment/asset keys and the filename. */
  q?: string;
  /** Narrow to one derived batch; omit it for the whole output. */
  batchNo?: number;
  /**
   * Narrow to one build state. Omit for every state.
   *
   * The output side's vocabulary, NOT the delivery side's: `pending`/`blocked`/
   * `building`/`ready`/`failed` here, against `active`/`voided` on
   * {@link ListDeliveryEntriesParams}. The two listings share nothing but the
   * parameter name, so a value must never be carried across them — the backend
   * answers a foreign value with a `400`, which is the fail-closed behaviour below.
   */
  state?: OutputBuildState;
  metadataStatus?: MetadataStatus;
}

/**
 * One keyset page of a output's entries (`GET /deliveries/:id/output/entries`),
 * narrowed to one derived batch by `?batchNo=` or spanning every batch without it.
 * Rows arrive in `(batchNo, ordinal)` order and each carries its own `batchNo`.
 *
 * This is the ONLY output-entries listing the client calls. There is a per-batch path
 * route (`.../batches/:batchNo/entries`) served by the same handler with the same
 * envelope, `?q=` matching, and composite `(batchNo, ordinal, entryId)` cursor — the
 * contract prefers this flat form, and one function taking an optional `batchNo` is
 * what lets the workspace treat the batch as a filter rather than as two different
 * listings. Passing a `?batchNo=` alongside that path parameter is a 400 by design (no
 * precedence rule), which the flat route cannot get wrong.
 *
 * `cursor` is the previous page's opaque `nextCursor` — omit it for the first page. It
 * encodes where the *filtered* scan stopped, so it is not interchangeable across
 * `batchNo`/`state`/`q` values; echo back only the one this same call returned (the
 * backend 400s an undecodable one rather than restarting the listing).
 *
 * Every filter here is fail-closed server-side — an unrecognized `state`, an
 * undecodable `cursor`, a negative `batchNo` are each a `400`, never a wider page. So
 * no caller may answer such a rejection by retrying with the parameter dropped: that
 * would answer a narrowed question with the unnarrowed listing.
 */
export async function listOutputEntries(
  deliveryId: string,
  { limit, cursor, q, batchNo, state, metadataStatus }: ListOutputEntriesParams = {},
  options: ApiCallOptions = {},
): Promise<OutputEntryPageResponse> {
  return visionLabApi.get<OutputEntryPageResponse>(
    `/deliveries/${enc(deliveryId)}/output-entries`,
    {
      ...options,
      searchParams: {
        limit,
        cursor: cursor || undefined,
        q: q || undefined,
        batchNo,
        state,
        metadataStatus,
      },
    },
  );
}

export interface ListCustomerOutputOrphansParams {
  limit?: number;
  cursor?: string | null;
  /** Narrow the audit to one derived batch; omit it for the whole output. */
  batchNo?: number;
}

/**
 * One keyset page of a output's candidate ORPHANED OUTPUTS
 * (`GET /deliveries/:id/output/outputs/orphans`) — objects an output receipt still
 * points at that nothing references any more. Admin-only (`.manage`).
 *
 * Two properties of this listing differ from every other one here and must survive
 * into the caller:
 *
 * - `complete: false` means at least one object ON THIS PAGE could not be probed, so
 *   the page is NOT a complete leak list. It has to be stated, never folded into a
 *   clean-looking result.
 * - An EMPTY `items` with a non-null `nextCursor` is NORMAL and paging must continue.
 *   The cursor is generated over the SQL candidate rows and the backend then drops the
 *   candidates its pure re-check rejects, so a page can come back empty with more
 *   candidates behind it. A `!items.length` stop condition would silently truncate the
 *   audit.
 *
 * `cursor` is opaque and, exactly as in {@link listOutputEntries}, encodes where the
 * *filtered* scan stopped — so it is not interchangeable across `batchNo` values and is
 * only ever echoed back verbatim.
 *
 * Every generation in the response is a decimal STRING and stays one (see
 * `outputOutputOrphanSchema`).
 */
export async function listCustomerOutputOrphans(
  deliveryId: string,
  { limit, cursor, batchNo }: ListCustomerOutputOrphansParams = {},
  options: ApiCallOptions = {},
): Promise<CustomerOutputOrphanPage> {
  return visionLabApi.get<CustomerOutputOrphanPage>(
    `/deliveries/${enc(deliveryId)}/outputs/orphans`,
    {
      ...options,
      searchParams: { batchNo, limit, cursor: cursor || undefined },
    },
  );
}

/**
 * DELETE the named entries' leaked objects
 * (`POST /deliveries/:id/output/outputs/orphans/purge`). Admin-only (`.manage`), and
 * the only destructive half of the audit.
 *
 * Each entry carries the `expectedGcsGeneration` the audit reported as its
 * `receiptGeneration`: it is a fence, so a rebuild between reading the audit and
 * confirming the purge answers `generation_fence_mismatch` and deletes nothing. Only a
 * `leaked` row may be sent — a `superseded` one names live content, and the backend
 * answers `not_purgeable` rather than destroying it.
 *
 * The response is PER-ENTRY and partial success is normal: it is a report to render,
 * not a success/failure to throw on. A 2xx says nothing about whether any given object
 * went away — read each `outcome`.
 */
export async function purgeCustomerOutputOrphans(
  deliveryId: string,
  input: PurgeOrphansInput,
  options: ApiCallOptions = {},
): Promise<PurgeOrphansResult> {
  const body = purgeOrphansInputSchema.parse(input);
  return visionLabApi.post<PurgeOrphansResult>(
    `/deliveries/${enc(deliveryId)}/outputs/orphans/purge`,
    { ...options, json: body },
  );
}

export async function fetchOutputReadLease(
  deliveryId: string,
  entryId: string,
  options: ApiCallOptions = {},
): Promise<OutputReadLease> {
  return visionLabApi.get<OutputReadLease>(
    `/deliveries/${enc(deliveryId)}/entries/${enc(entryId)}/read-lease`,
    { ...options, cache: 'no-store' },
  );
}

/**
 * Read a output entry's build task
 * (`GET /deliveries/:pid/output/entries/:id/task`). Returns the identical
 * `entryTaskJson` shape as the delivery-entry task read (`ownerDomain=
 * delivery_output_entry`). A 404 — output not found, or the entry is not a
 * member of the output, or there is no build task for the entry's current
 * build generation yet. The service layer maps that expected 404 to `null`.
 */
export async function fetchOutputEntryTask(
  deliveryId: string,
  entryId: string,
  options: ApiCallOptions = {},
): Promise<EntryTask> {
  return visionLabApi.get<EntryTask>(
    `/deliveries/${enc(deliveryId)}/entries/${enc(entryId)}/task`,
    options,
  );
}

export async function finalizeDelivery(deliveryId: string): Promise<DeliveryFinalizeResult> {
  return visionLabApi.post<DeliveryFinalizeResult>(`/deliveries/${enc(deliveryId)}/finalize`, {
    json: {},
  });
}
